import { useEffect, useRef } from "react";

import { TenantProfileDto } from "@/core/api/generated/v1";
import { fetchTenantProfile } from "@/store/tenantProfile/slice";

import { useAuthenticationInfo } from "../../auth/useAuthenticationInfo";
import { useAppSelector, useAppThunkDispatch } from "../../redux";
import { useCurrentTenantInfoV2 } from "./useCurrentTenantInfoV2";

interface Props {
  /** Refetch only on init if already fetched. */
  isRefetchOnInit?: boolean;
  isSkipFetch?: boolean;
}

export interface UseTenantProfileV2HookResult {
  /** Whether the result is initialized and ready for usage. */
  isInited: boolean;
  profile: Nil<TenantProfileDto>;
  tenantId: Nil<string>;
}

/** Returns currently resolved tenant profile (loads it if not loaded). */
export function useTenantProfileV2(props?: Props): UseTenantProfileV2HookResult {
  const dispatch = useAppThunkDispatch();

  const authenticationInfo = useAuthenticationInfo();
  const tenantInfo = useCurrentTenantInfoV2();
  const isProfileFetched = useAppSelector((x) => x.tenantProfile.isProfileFetched);
  const tenantProfile = useAppSelector((x) => x.tenantProfile.profile);

  const resultRef = useRef<UseTenantProfileV2HookResult>({
    isInited: false,
    profile: null,
    tenantId: null,
  });

  if (!resultRef.current.isInited && tenantInfo.isInited && !tenantInfo.isTenantContext) {
    resultRef.current.isInited = true;
    resultRef.current.profile = null;
  } else if (
    isProfileFetched &&
    (!resultRef.current.isInited || resultRef.current.profile !== tenantProfile)
  ) {
    resultRef.current.isInited = true;
    resultRef.current.profile = tenantProfile;
    resultRef.current.tenantId = tenantProfile?.id;
  }

  useEffect(() => {
    // NB: only fetch if authenticated to avoid unnecessary requests and redirects to login on 401
    if (
      authenticationInfo.isAuthenticated &&
      tenantInfo.isInited &&
      tenantInfo.isTenantContext &&
      !tenantProfile &&
      !props?.isSkipFetch
    ) {
      dispatch(fetchTenantProfile({ isRefetch: props?.isRefetchOnInit }));
    }
  }, [authenticationInfo.isAuthenticated, tenantInfo.isInited, props?.isSkipFetch]);

  return resultRef.current;
}
